import * as React from 'react';
import { cn } from '@/lib/utils';

// shadcn-style Card primitives. The default surface is `bg-card/70` +
// `backdrop-blur-md` so cards layered over the dashboard's gradient mesh
// pick up the glassmorphic effect without each consumer having to set it.
// Override `className` for solid surfaces.

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // macOS-style vibrant glass surface. Three things make it read as
      // "glass" rather than "frosted plastic":
      //   1. High blur + saturate boosts colours behind the panel so the
      //      gradient backdrop bleeds through with vibrancy.
      //   2. Low-opacity background tint keeps the surface translucent;
      //      dark-mode uses a near-black with very low alpha so the
      //      gradient still reads through.
      //   3. A subtle inner highlight on the top edge (the ::before band)
      //      mimics how Sonoma / Sequoia panels catch light, separating the
      //      panel from the surface beneath without a hard border line.
      'relative overflow-hidden rounded-2xl text-card-foreground',
      'border border-white/45 bg-white/60 dark:border-white/10 dark:bg-card/85',
      'backdrop-blur-2xl backdrop-saturate-150',
      // Drop shadow + a 1px inset highlight on the top edge that reads as a
      // glass bevel under directional light.
      'shadow-[0_1px_2px_rgba(15,23,42,0.04),0_22px_60px_-20px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.7)]',
      'dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_22px_60px_-20px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.08)]',
      // ::before — top-down sheen. Soft white fade from the top edge dying
      // out around 35% of the panel height. Light → frosted; this → wet.
      'before:pointer-events-none before:absolute before:inset-0',
      'before:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.55)_0%,rgba(255,255,255,0.18)_35%,rgba(255,255,255,0)_60%)]',
      'dark:before:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.10)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_60%)]',
      // ::after — diagonal specular catch from top-left, plus-lighter blend
      // so it stacks additively over the sheen and the surface beneath
      // without darkening anything.
      'after:pointer-events-none after:absolute after:inset-0 after:[mix-blend-mode:plus-lighter]',
      'after:bg-[linear-gradient(135deg,rgba(255,255,255,0.45)_0%,rgba(255,255,255,0.15)_28%,rgba(255,255,255,0)_60%)]',
      // Over the dashboard gradient the additive specular sheen lifts light
      // onto the card text in dark mode — gate it off there (light untouched).
      'dark:after:hidden',
      // Hoist direct children above the sheen layer. ::after by default
      // paints OVER the content because of source-order stacking — without
      // this, plus-lighter would tint text. Children need their own
      // stacking context to win.
      '[&>*]:relative [&>*]:z-10',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-1 p-5 pb-3', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-base font-semibold leading-tight tracking-tight',
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-5 pt-2', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-5 pt-3', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
